import { useState, useEffect } from "react";
import C from "classnames";

// PrimeReact Components
import { ProgressSpinner } from "primereact/progressspinner";
import { Card } from "primereact/card";

// Toolkit imports
import { retrieveAuthToken } from "sj-ui-toolkit/src/utils";
import { themeClasses } from "sj-ui-toolkit/src/atlas/utils";

// App imports
import LessonPage from "../Lesson/LessonPage";

// Component-level imports
import S from "./GuestLessonAccess.module.scss";

type VerificationStatus = "verifying" | "success" | "error";

// Main component
const GuestVerifyPage = () => {
  const [verificationStatus, setVerificationStatus] = useState<VerificationStatus>("verifying");
  const [errorMessage, setErrorMessage] = useState("");

  // On component mount, verify guest access
  useEffect(() => {
    const verifyGuestAccess = async () => {
      try {
        const token = await retrieveAuthToken();
        if (!token) throw new Error("We could not verify your access at this time.");
      } catch (error) {
        setVerificationStatus("error");
        setErrorMessage(
          (error as Error).message || "An unknown error occurred during verification."
        );
        return;
      }

      setVerificationStatus("success");
    };
    verifyGuestAccess();
  }, []);

  // Render different states based on verification status
  switch (verificationStatus) {
    case "success":
      return (
        <div className={C("atlas-bg-lesson", themeClasses.flexLayout)}>
          <main className={C(themeClasses.flexMain, S.lessonMain)}>
            <div className={themeClasses.guestLessonContainer}>
              <LessonPage />
            </div>
          </main>
        </div>
      );

    case "error":
      return (
        <div className={C("atlas-bg-lesson", S.container)}>
          <Card className={S.card}>
            <div className={S.errorContent}>
              <div className={S.errorIcon}>✕</div>
              <h2 className={S.title}>Verification Failed</h2>
              <p className={S.message}>{errorMessage || "Unable to verify your access."}</p>
            </div>
          </Card>
        </div>
      );

    case "verifying":
    default:
      return (
        <div className={C("atlas-bg-lesson", S.container)}>
          <Card className={S.card}>
            <div className={S.verifyingContent}>
              <ProgressSpinner />
              <h2 className={S.title}>Verifying Access</h2>
              <p className={S.message}>Please wait while we verify your access...</p>
            </div>
          </Card>
        </div>
      );
  }
};

export default GuestVerifyPage;
